import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { SkillListScope, SkillSummary } from "../SkillRecord";

export const skillsQueryKey = (
  orgId: string | undefined,
  scope?: SkillListScope,
) => (scope === undefined ? ["skills", orgId] : ["skills", orgId, scope]);

interface UseSkillsResult {
  items: SkillSummary[];
}

/**
 * Fetch the current org's visible skills (org-shared + the caller's private).
 *
 * Pagination: backend returns up to 1000 per page with a `next_token`. This
 * hook follows the token internally so callers see a single flat list — orgs
 * with > 1000 skills surface the full set rather than a silent truncation.
 *
 * Pass `scope="personal"` to restrict to skills the caller authored or has
 * subscribed to, or `scope="org"` for org-shared skills the caller did not
 * author (regardless of subscription state). Omit `scope` to receive every
 * visible skill.
 */
export function useSkills(orgId: string | undefined, scope?: SkillListScope) {
  const { skillService } = useDomainServices();

  return useQuery<UseSkillsResult>({
    queryKey: skillsQueryKey(orgId, scope),
    queryFn: async ({ signal }) => {
      const items: SkillSummary[] = [];
      let pageToken: string | undefined;
      do {
        const page = await skillService.listSkills({
          abortSignal: signal,
          orgId,
          pageToken,
          scope,
        });
        items.push(...page.items);
        pageToken = page.next_token ?? undefined;
      } while (pageToken !== undefined);
      return { items };
    },
    enabled: orgId !== undefined,
    staleTime: 30_000,
  });
}
